/**
 * Architectural Lint dashboard (doc 147 §6.2 + PR Q2 sidebar/mute).
 *
 * Runs `runLint` against the loaded manifest in-process — no backend
 * roundtrip. PR Q2 adds:
 *  - Rule library sidebar with descriptions + per-rule sparklines
 *  - Click a rule to live-filter findings
 *  - Mute · 30d (persisted via qualityPersistence; muted findings hidden
 *    by default with a toggle to reveal)
 *  - "Fix in Sandbox" CTA on each finding (wired in Q4)
 */
import { useMemo, useState } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import {
  runLint,
  type LintFinding,
  type LintSeverity,
} from '@atlas-manifest/lint';
import { Icon } from '../components/Icon.js';
import { ErrorState } from '../components/ErrorState.js';
import { useFocusScroll } from '../state/useFocusScroll.js';
import { FindingCard, type FindingSeverity, type FindingAction } from '../components/FindingCard.js';
import {
  ruleLibrary,
  filterMuted,
  muteKey,
  type Mute,
} from '../state/lintLibrary.js';
import {
  readMutes,
  writeMute,
  clearMute,
} from '../state/qualityPersistence.js';
import { useInlineError, persistErrorMessage } from '../state/useInlineError.js';
import { navigate } from '../state/hashRouter.js';

export interface LintDashboardViewProps {
  manifest: AtlasManifest;
  failOn?: LintSeverity;
  onNavigateEntity: (entityId: string) => void;
  /** Optional entity id to scroll to + highlight (from `#lint/focus/<id>`). */
  focus?: string;
}

const MUTE_30D_MS = 30 * 24 * 60 * 60 * 1000;
const SEVERITIES: LintSeverity[] = ['high', 'medium', 'low'];

/** Map LintSeverity → FindingCard severity (different vocab). */
function toFindingSeverity(s: LintSeverity): FindingSeverity {
  if (s === 'high') return 'high';
  if (s === 'medium') return 'med';
  return 'low';
}

export function LintDashboardView({
  manifest,
  failOn = 'high',
  onNavigateEntity,
  focus,
}: LintDashboardViewProps): React.JSX.Element {
  const [activeRule, setActiveRule] = useState<string>('all');
  const [showMuted, setShowMuted] = useState(false);
  const [mutes, setMutes] = useState<Record<string, Mute>>(() => readMutes());
  const [error, showError, clearError] = useInlineError();
  useFocusScroll(focus);

  const report = useMemo(() => runLint(manifest, { failOn }), [manifest, failOn]);
  const library = useMemo(() => ruleLibrary(manifest), [manifest]);

  const now = Date.now();
  const visibleFindings = useMemo(() => {
    let f = report.findings;
    if (activeRule !== 'all') f = f.filter((x) => x.ruleId === activeRule);
    if (!showMuted) f = filterMuted(f, mutes, now);
    return f;
  }, [report.findings, activeRule, mutes, showMuted, now]);

  const counts = {
    high: report.bySeverity.high.length,
    medium: report.bySeverity.medium.length,
    low: report.bySeverity.low.length,
  };

  const entityIds = new Set(manifest.entities.map((e) => e.id));
  const refToEntity = (ref: string): string | undefined => {
    if (entityIds.has(ref)) return ref;
    const dot = ref.indexOf('.');
    if (dot > 0 && entityIds.has(ref.slice(0, dot))) return ref.slice(0, dot);
    return undefined;
  };

  const mute = (f: LintFinding): void => {
    const key = muteKey(f.ruleId, f.ref);
    const m: Mute = { mutedUntil: now + MUTE_30D_MS, mutedBy: 'me', reason: '' };
    const r = writeMute(key, m);
    if (!r.ok) { showError(persistErrorMessage(r, 'save mute')); return; }
    setMutes({ ...mutes, [key]: m });
  };
  const unmute = (f: LintFinding): void => {
    const key = muteKey(f.ruleId, f.ref);
    const r = clearMute(key);
    if (!r.ok) { showError(persistErrorMessage(r, 'clear mute')); return; }
    const next = { ...mutes };
    delete next[key];
    setMutes(next);
  };

  const muteCount = Object.values(mutes).filter((m) => m.mutedUntil > now).length;

  return (
    <div className="lint-dashboard">
      {error && <ErrorState message={error} onDismiss={clearError} />}
      <header className="lint-head">
        <h2>Architectural Lint</h2>
        <dl className="lint-summary" aria-label="Summary">
          {SEVERITIES.map((s) => (
            <div key={s} className={`lint-summary-cell tone-${s}${counts[s] === 0 ? ' is-empty' : ''}`}>
              <dt>{s.toUpperCase()}</dt>
              <dd>{counts[s]}</dd>
            </div>
          ))}
        </dl>
        {muteCount > 0 && (
          <button
            type="button"
            className={`btn btn-ghost lint-mute-toggle${showMuted ? ' is-active' : ''}`}
            onClick={() => setShowMuted((v) => !v)}
            aria-pressed={showMuted}
          >
            <Icon name="notifications_off" size="sm" />
            {showMuted ? 'Hide' : 'Show'} muted · {muteCount}
          </button>
        )}
      </header>

      {report.ok ? (
        <div className="lint-banner lint-banner-ok">
          <Icon name="check_circle" size="md" />
          <span>No findings at or above <code>{failOn}</code> severity.</span>
        </div>
      ) : (
        <div className="lint-banner lint-banner-fail">
          <Icon name="warning" size="md" />
          <span>{counts.high} HIGH-severity findings would fail CI.</span>
        </div>
      )}

      <div className="lint-layout">
        <aside className="rule-side" aria-label="Rule library">
          <h3 className="rule-side-head">Rule library · {library.length}</h3>
          <button
            type="button"
            className={`rule-row${activeRule === 'all' ? ' is-active' : ''}`}
            onClick={() => setActiveRule('all')}
          >
            <span>All rules</span>
            <span className="rule-row-count mono">{report.findings.length}</span>
          </button>
          {library.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`rule-row${activeRule === r.id ? ' is-active' : ''}`}
              onClick={() => setActiveRule(r.id)}
              title={r.description}
            >
              <span className="rule-row-name">
                <code>{r.id}</code>
                <span className={`rule-spark tone-${toFindingSeverity(r.severity)}`}>
                  {r.history.map((c, i) => (
                    <span key={i} style={{ height: Math.max(2, Math.min(12, c * 2)) + 'px' }} />
                  ))}
                </span>
              </span>
              <span className="rule-row-count mono">{r.currentCount}</span>
            </button>
          ))}
          <p className="rule-side-foot">
            Click a rule above to live-filter findings.
          </p>
        </aside>

        <section className="lint-findings" aria-label="Findings">
          {visibleFindings.length === 0 ? (
            <div className="lint-banner lint-banner-ok">
              <Icon name="check_circle" size="md" />
              <span>
                {activeRule === 'all'
                  ? 'No findings to show.'
                  : `No findings for rule "${activeRule}".`}
              </span>
            </div>
          ) : (
            <ul className="lint-findings-list">
              {visibleFindings.map((f, i) => {
                const target = refToEntity(f.ref);
                const key = muteKey(f.ruleId, f.ref);
                const isMuted = mutes[key] && mutes[key]!.mutedUntil > now;
                const actions: FindingAction[] = [
                  {
                    label: 'Fix in Sandbox',
                    variant: 'primary' as const,
                    onClick: () =>
                      navigate(target ? { kind: 'sandbox', seedEntity: target } : { kind: 'sandbox' }),
                  },
                  ...(target
                    ? [{
                        label: `Open ${target}`,
                        onClick: () => onNavigateEntity(target),
                      }]
                    : []),
                  {
                    label: 'Mute · 30d',
                    onClick: () => mute(f),
                  },
                ];
                return (
                  <li
                    key={`${f.ruleId}-${f.ref}-${i}`}
                    {...(target ? { 'data-entity-id': target } : {})}
                  >
                    <FindingCard
                      title={`[${f.ruleId}] ${f.ref}`}
                      body={f.message}
                      severity={toFindingSeverity(f.severity)}
                      rule={f.ruleId}
                      entityRefs={target ? [target] : []}
                      {...(isMuted ? {
                        mutedUntil: mutes[key]!.mutedUntil,
                        onUnmute: () => unmute(f),
                      } : {})}
                      actions={actions}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
